import React, { ReactNode } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { alpha, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useDocument } from '@common/hooks/useDocument';
import { lightTheme as lightMuiTheme, darkTheme as darkMuiTheme } from './common/theme';
import { AppDispatch, showTelnyxFeatures, State } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { lightTheme, darkTheme } from './styles/theme';
import { logout } from '@store/async';
import { ThemeManager } from './common/ThemeManager';

import { Workflows } from '@components/Workspace/routes/Workflows';
import { Buckets } from '@components/Workspace/routes/Buckets';
import { Bucket } from '@components/Workspace/routes/Buckets/Bucket';
import { Secrets } from '@components/Workspace/routes/Secrets';
import { NotFound } from '@components/Workspace/routes/Fallback/NotFound';
import { File } from '@components/Workspace/routes/Buckets/Bucket/File/File';
import { PermissionGroups } from '@components/Workspace/routes/Permissions/PermissionGroups';

import { Workspaces } from '@components/Workspaces';
import { Docs } from '@components/Docs';
import { Login } from '@components/Login';
import { Flow } from '@components/Flow';
import { Reporting } from '@components/Reporting';

import { BeginnerRightClickInfo } from '@components/Modals/BeginnerRightClickInfo';
import { WorkflowExecuteInfo } from '@components/Modals/WorkflowExecuteInfo';
import { UnsavedChanges } from '@components/Modals/UnsavedChanges';
import { AddDataPrefix } from '@components/Modals/AddDataPrefix';
import { GlobalVariables } from '@components/Workspace/routes/GlobalVariables';
import { ReadCreateEditGlobalVariable } from '@components/Modals/ReadCreateEditGlobalVariable';
import { Admin } from '@components/Admin/Admin';
import { DeleteGlobalVariable } from '@components/Modals/DeleteGlobalVariable';
import { Assistants } from '@components/Workspace/routes/Assistants/Assistants';

import {
  CreateBucket,
  DeleteBucket,
  SyncBucket,
  CloseTab,
  CreateService,
  DeleteService,
  DeleteTab,
  CreateSecret,
  DeleteSecret,
} from '@modals/index';

const Container = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.background.flow};

  .react-flow {
    background-color: ${({ theme }) => theme.background.flow};
    font-family: 'Inter' !important;
  }
`;

const LoggedInAs = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background-color: ${({ theme }) => theme.telnyx.orange};
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  border-radius: 0 0 5px 5px;
  padding: 5px 9px;
  font-family: Inter;

  #username {
    text-decoration: underline;
    font-weight: bold;
    margin-left: 4px;
  }

  button {
    margin-left: 0.75rem;
    font-family: Inter;
    font-size: 12px;
    background-color: #c15129;
    border-radius: 3px;
    color: white;
    border: 0;
    padding: 2px 6px;
    font-size: 11px;
  }

  button:hover {
    cursor: pointer;
    background-color: ${() => alpha('#000000', 0.8)};
  }
`;

const AuthRequiredRoutes = () => {
  const location = useLocation();

  return (
    <Routes>
      <Route path='/login' element={INTERNAL_CLIENT ? <Admin /> : <Login />} />
      <Route path='*' element={<Navigate to='/login' state={{ from: location.pathname }} replace />} />
    </Routes>
  );
};

const Wrapper: React.FC<{ route: ReactNode; modals?: boolean }> = ({ route, modals = false }) => {
  const dispatch = useDispatch<AppDispatch>();
  const email = useSelector((state: State) => state.auth.email);

  return (
    <div>
      {INTERNAL_CLIENT && (
        <LoggedInAs>
          Logged in as <span id='username'>{email}</span>
          <button onClick={() => dispatch(logout())}>Logout</button>
        </LoggedInAs>
      )}
      {route}
      {modals && (
        <>
          <DeleteBucket />
          <CreateBucket />
          <SyncBucket />
          <CloseTab />
          <CreateService />
          <DeleteService />
          <DeleteTab />
          <CreateSecret />
          <DeleteSecret />
          <ReadCreateEditGlobalVariable />
          <DeleteGlobalVariable />
          <WorkflowExecuteInfo />
          <UnsavedChanges />
          <AddDataPrefix />
          <BeginnerRightClickInfo />
        </>
      )}
    </div>
  );
};

const AuthRoutes: React.FC = () => {
  const loader = useDocument();
  const isTelnyxUser = useSelector((state: State) => showTelnyxFeatures(state));

  return (
    <Routes>
      <Route path='/documentation/:doc_id' element={<Docs />} />
      <Route path='/documentation/*' element={<Docs />} />

      <Route path='/' element={<Wrapper route={<Workspaces />} modals />} />

      {/* flow/Flow.tsx (BottomToolbar, TopToolbar, ToolbarMenu) */}
      <Route path='/:service_id/workflow/:workflow_id' element={<Wrapper route={<Flow />} modals />} />

      {/* workspace/routes/Workflows.tsx */}
      <Route path='/:service_id/workflow' element={<Wrapper route={<Workflows type='workflow' />} modals />} />
      <Route path='/:service_id/modules' element={<Wrapper route={<Workflows type='module' />} modals />} />

      {/* workspace/routes/Permissions.tsx */}
      {/* <Route path='/:service_id/permissions' element={<Wrapper route={<WorkspacePermissions />} modals />} /> */}
      <Route path='/:service_id/permissions' element={<Wrapper route={<PermissionGroups />} modals />} />

      {/* workspace/routes/Secrets.tsx */}
      <Route path='/:service_id/secrets' element={<Wrapper route={<Secrets />} modals />} />

      {/* workspace/routes/GlobalVariables.tsx */}
      <Route path='/:service_id/global-variables' element={<Wrapper route={<GlobalVariables />} modals />} />

      {/* workspace/routes/Buckets.tsx (mRawEditor, Bucket, File) */}
      <Route path='/:service_id/buckets' element={<Wrapper route={<Buckets documentLoader={loader} />} modals />} />
      <Route path='/:service_id/buckets/:bucketName/:fileName' element={<Wrapper route={<File />} modals />} />
      <Route
        path='/:service_id/buckets/:bucketName'
        element={<Wrapper route={<Bucket documentLoader={loader} />} modals />}
      />

      {isTelnyxUser && (
        <Route path='/:service_id/assistants/:assistantId?/:tab?' element={<Wrapper route={<Assistants />} modals />} />
      )}

      <Route path='/:service_id/executions' element={<Wrapper route={<Reporting />} />} />

      <Route path='/:service_id/:other' element={<Wrapper route={<NotFound />} />} />

      {/* <Route path='/specification' element={<Specification />} /> */}
      {/* <Route path='/logs' element={<Logs />} /> */}

      <Route path='/error' element={<Navigate to='/' />} />
      <Route path='/*' element={<Navigate to='/' />} />
    </Routes>
  );
};

const AppRoutes: React.FC = () => {
  const isLoggedIn = useSelector((state: State) => state.auth.isLoggedIn);

  if (!isLoggedIn) return <AuthRequiredRoutes />;
  return <AuthRoutes />;
};

export const App: React.FC = () => {
  const theme = useSelector((state: State) => state.flow.theme);

  return (
    <MuiThemeProvider theme={theme === 'light' ? lightMuiTheme : darkMuiTheme}>
      <ThemeProvider theme={theme === 'light' ? lightTheme : darkTheme}>
        <ThemeManager />
        <Container>
          <Router>
            <AppRoutes />
          </Router>
        </Container>
      </ThemeProvider>
    </MuiThemeProvider>
  );
};
